import React, { useState, useEffect, useContext } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from "react-native";
import { Play, Pause } from "lucide-react";
import { mixpanel } from "../../../../../utils/mixpanel";
import { SessionContext } from "../../../../../contexts";
import EMRSyncModal from "../EMRSyncModal";
import { listIntegrations } from "../../../../../services/api-integrations";
import {
  reprocessAudioAsDifferentNoteType,
  syncNoteWithIntegration,
  getPatientNotes,
  signPatientNote,
  createClinicNoteSignature,
  getClinicNoteSignature,
  updateClinicNoteSignature,
} from "../../../../../services/api-documentation";
import {
  generatePDFDocument,
  generatePatientNoteFilename,
} from "../../../../../utils/fileUtils";
import {
  formatDate,
  formatTime,
  formatTodayDate,
} from "../../../../../utils/timeUtils";
import { logUIError } from "../../../../../services/api-misc";
import { useResponsiveColumns } from "../../../../../utils/useResponsiveColumns";

// Onboarding
import { useOnboardingTips } from "../../../../../contexts/OnboardingTipsContext";
import { TOOLTIP_STEPS } from "../../../../../utils/constant";
import EncounterTypeModal from "../encounter-type/EncounterTypeModal";
import { useEncounterTypes } from "../encounter-type/useEncounterTypes";

// Custom Hooks
import { useVoiceNotes } from "./hooks/UseVoiceNotes";
import { useAudioPlayer } from "./hooks/UseAudioPlayer";
import { useNoteEditor } from "./hooks/UseNoteEditor";

// Components
import {
  VoiceNoteCard,
  SignatureModal,
  DeleteConfirmModal,
  IncompleteChunkRecoveryModal,
} from "./components";

// Types
import { VoiceNote, Patient, VoiceNotesListProps } from "./types/VoiceNotes";
import { shouldOfferIncompleteChunkRecovery } from "./chunkedAudioRecovery";

const NOTE_SIGNATURE_STORAGE_PREFIX = "clinic-note-signatures";

const buildSignatureStorageKey = (clinicId: string) =>
  `${NOTE_SIGNATURE_STORAGE_PREFIX}:${clinicId}`;

const normalizeSignature = (value: string) => value.trim();

const dedupeSignatures = (values: string[]): string[] => {
  const seen = new Set<string>();
  const results: string[] = [];

  values.forEach((value) => {
    const normalized = normalizeSignature(value);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    results.push(normalized);
  });

  return results;
};

const readStoredSignatures = (clinicId: string): string[] => {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(buildSignatureStorageKey(clinicId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return dedupeSignatures(parsed.filter((item) => typeof item === "string"));
  } catch (error) {
    console.warn("Failed to read stored signatures", error);
    return [];
  }
};

const writeStoredSignatures = (clinicId: string, values: string[]) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    const normalized = dedupeSignatures(values);
    window.localStorage.setItem(
      buildSignatureStorageKey(clinicId),
      JSON.stringify(normalized),
    );
  } catch (error) {
    console.warn("Failed to store signatures", error);
  }
};

const VoiceNotesList: React.FC<VoiceNotesListProps> = ({
  patient,
  appointmentId,
  refreshKey = 0,
  tipsEnabled = false,
}) => {
  const getTodayLocalISODate = (): string => {
    const now = new Date();
    const tzOffsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 10);
  };

  const toISODate = (value?: string | null): string => {
    if (!value) return getTodayLocalISODate();
    return value.includes("T") ? value.split("T")[0] : value;
  };

  const toDateTimeLocalValue = (value?: string | null): string => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    const tzOffsetMs = parsed.getTimezoneOffset() * 60000;
    return new Date(parsed.getTime() - tzOffsetMs).toISOString().slice(0, 16);
  };

  const session = useContext(SessionContext)?.session;

  const logVoiceNotesUIError = async (
    message: string,
    extraContext?: Record<string, unknown>,
  ) => {
    try {
      await logUIError({
        message,
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        user_email: session?.userEmail || undefined,
        clinic_name: session?.clinicName || undefined,
        clinic_id: session?.clinicId || undefined,
        context: {
          component: "VoiceNotesList",
          patientId: patient?.clinicOSUUID,
          appointmentId,
          ...extraContext,
        },
      });
    } catch (err) {
      console.warn("logVoiceNotesUIError failed", err);
    }
  };

  // Don't render anything if session is not available
  if (!session) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Refresh the page</Text>
        </View>
      </View>
    );
  }

  // Custom Hooks
  const {
    voiceNotes,
    unprocessedAudios,
    loading,
    saving,
    error,
    fetchVoiceNotes,
    updateNote,
    deleteNote,
  } = useVoiceNotes(patient, session, refreshKey);

  const { playingNoteId, loadingAudio, handlePlayPause, setLoadingAudio } =
    useAudioPlayer(patient, session);

  const {
    editingField,
    editingNoteId,
    editedSummary,
    expandedSummaries,
    signedNotes,
    handleEditSummary,
    handleCancelEdit,
    toggleSummary,
    setEditedSummary,
    setSignedNotes,
  } = useNoteEditor(patient, session);

  const { containerStyle: notesContainerStyle, getItemStyle } =
    useResponsiveColumns({
      breakpoint: 1800,
      maxItemWidth: 800,
      gap: 12,
    });

  // Local component state
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [noteToDelete, setNoteToDelete] = useState<VoiceNote | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [signatureDateTimeOverride, setSignatureDateTimeOverride] =
    useState("");
  const [savedSignatures, setSavedSignatures] = useState<string[]>([]);
  const [selectedSavedSignature, setSelectedSavedSignature] = useState("");
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [signatureRecordId, setSignatureRecordId] = useState<number | null>(
    null,
  );
  const [currentNoteForSignature, setCurrentNoteForSignature] =
    useState<VoiceNote | null>(null);
  const [signingInProgress, setSigningInProgress] = useState(false);
  const [emrSyncModalVisible, setEmrSyncModalVisible] = useState(false);
  const [selectedNoteForSync, setSelectedNoteForSync] =
    useState<VoiceNote | null>(null);
  const [hasCompatibleIntegration, setHasCompatibleIntegration] =
    useState(false);
  const [checkingIntegrations, setCheckingIntegrations] = useState(true);
  const [reprocessModalVisible, setReprocessModalVisible] = useState(false);
  const [reprocessTargetNote, setReprocessTargetNote] =
    useState<VoiceNote | null>(null);
  const [reprocessNoteDate, setReprocessNoteDate] = useState<string>(() =>
    getTodayLocalISODate(),
  );
  const [reprocessingNoteId, setReprocessingNoteId] = useState<string | null>(
    null,
  );
  const [reprocessProgressText, setReprocessProgressText] = useState<
    string | null
  >(null);
  const [recoverIncompleteChunkedUpload, setRecoverIncompleteChunkedUpload] =
    useState(false);
  const [recoveryConfirmAudio, setRecoveryConfirmAudio] = useState<
    (typeof unprocessedAudios)[number] | null
  >(null);
  const {
    available: availableNoteTypes,
    loading: loadingNoteTypes,
    error: noteTypesError,
    createType: createNoteType,
  } = useEncounterTypes(session);

  // Onboarding tips
  const { tipsState, markStepComplete } = useOnboardingTips();

  const trimmedSignature = normalizeSignature(signatureName);
  const isSignatureSaved = Boolean(
    trimmedSignature &&
      savedSignatures.some(
        (value) => value.toLowerCase() === trimmedSignature.toLowerCase(),
      ),
  );
  const canSaveSignature = Boolean(trimmedSignature) && !isSignatureSaved;

  // Check for compatible integrations
  useEffect(() => {
    const checkForCompatibleIntegration = async () => {
      if (!session?.accessToken) return;

      setCheckingIntegrations(true);
      try {
        const integrations = await listIntegrations(session);
        const emrIntegrationTypes = ["drchrono"];
        const hasEMRIntegration = integrations.some(
          (integration) =>
            emrIntegrationTypes.includes(integration.integration_type) &&
            integration.is_active,
        );
        setHasCompatibleIntegration(hasEMRIntegration);
      } catch (error) {
        console.error("Error checking integrations:", error);
        setHasCompatibleIntegration(false);
      } finally {
        setCheckingIntegrations(false);
      }
    };

    checkForCompatibleIntegration();
  }, [session]);

  useEffect(() => {
    if (!showSignatureModal || !session?.clinicId) return;
    const clinicId = session.clinicId;

    let isActive = true;
    setSignatureError(null);
    setLoadingSignatures(true);

    const cached = readStoredSignatures(clinicId);
    setSavedSignatures(cached);

    const loadSignature = async () => {
      try {
        const signature = await getClinicNoteSignature(session);
        if (!isActive) return;
        if (signature?.signature) {
          setSignatureRecordId(signature.id);
          setSavedSignatures((prev) => {
            const merged = dedupeSignatures([signature.signature, ...prev]);
            writeStoredSignatures(clinicId, merged);
            return merged;
          });
          setSignatureName((current) =>
            current.trim() ? current : signature.signature,
          );
        }
      } catch (error) {
        if (!isActive) return;
        setSignatureError("Unable to load saved signatures.");
        logVoiceNotesUIError("Clinic note signature load failed", {
          error: error instanceof Error ? error.message : String(error),
        });
      } finally {
        if (isActive) setLoadingSignatures(false);
      }
    };

    loadSignature();

    return () => {
      isActive = false;
    };
  }, [showSignatureModal, session?.clinicId]);

  useEffect(() => {
    if (!signatureName.trim()) {
      setSelectedSavedSignature("");
      return;
    }

    const match = savedSignatures.find(
      (value) => value.toLowerCase() === signatureName.trim().toLowerCase(),
    );
    setSelectedSavedSignature(match || "");
  }, [signatureName, savedSignatures]);

  // Utility function
  const getNoteTypeLabel = (type: string): string => {
    if (!type) {
      return "Voice Note";
    }

    const normalized = type.trim().toLowerCase();

    switch (normalized) {
      case "consultation":
        return "Consultation Note";
      case "phone call":
        return "Phone Call";
      case "follow up":
      case "follow-up":
        return "Follow-up Visit";
      case "markings":
        return "Markings";
      default:
        return type;
    }
  };

  // Event handlers
  const handleSyncToEMR = (note: VoiceNote) => {
    if (!note.note_json) {
      Alert.alert(
        "Error",
        "This note cannot be synced - no structured data available",
      );
      return;
    }
    if (!appointmentId) {
      Alert.alert("Error", "No appointment ID available for sync");
      return;
    }
    setSelectedNoteForSync(note);
    setEmrSyncModalVisible(true);
  };

  const handleUploadToEMR = async (note: VoiceNote) => {
    try {
      mixpanel.track("Voice Note EMR Upload Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });

      if (!patient.clinicOSUUID || !session?.clinicId) {
        Alert.alert("Error", "Missing information needed to upload note");
        return;
      }

      await syncNoteWithIntegration(
        session,
        session.clinicId,
        patient.clinicOSUUID,
        parseInt(note.id),
      );

      Alert.alert("Success", "Note uploaded to EMR successfully");

      mixpanel.track("Voice Note EMR Upload Success", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });
    } catch (error) {
      console.error("Failed to upload note to EMR:", error);
      Alert.alert("Error", "Failed to upload note to EMR. Please try again.");

      mixpanel.track("Voice Note EMR Upload Failed", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
        Error: error instanceof Error ? error.message : "Unknown error",
      });

      logVoiceNotesUIError("Voice note EMR upload failed", {
        noteId: note.id,
        noteType: note.type,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const downloadTranscript = (note: VoiceNote): void => {
    try {
      mixpanel.track("Voice Note Transcript Download Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });

      const content = note.transcription || "No transcript available";
      const blob = new Blob([content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = generatePatientNoteFilename(
        patient.name,
        getNoteTypeLabel(note.type),
        formatDate(note.date),
        "Transcript",
        "txt",
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      mixpanel.track("Voice Note Transcript Downloaded", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });
    } catch (error) {
      console.error("Error downloading transcript:", error);
      Alert.alert("Error", "Failed to download transcript. Please try again.");

      mixpanel.track("Voice Note Transcript Download Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Error Message":
          error instanceof Error ? error.message : "Unknown error",
      });

      logVoiceNotesUIError("Transcript download failed", {
        noteId: note.id,
        noteType: note.type,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const downloadAudio = async (note: VoiceNote): Promise<void> => {
    try {
      if (!note.url) {
        Alert.alert("Error", "Audio file not available for download");
        return;
      }

      const getAudioExtensionFromUrl = (url: string): string => {
        try {
          const urlObj = new URL(url);
          const extension = urlObj.pathname.split(".").pop()?.toLowerCase();
          const audioExtensions = [
            "mp3",
            "wav",
            "aac",
            "ogg",
            "flac",
            "m4a",
            "webm",
            "mp4",
          ];
          return audioExtensions.includes(extension || "") ? extension! : "mp3";
        } catch {
          return "mp3";
        }
      };

      const getAudioExtensionFromContentType = (
        contentType: string | null,
      ): string | null => {
        if (!contentType) return null;
        const normalized = contentType.toLowerCase();
        if (normalized.includes("audio/webm")) return "webm";
        if (
          normalized.includes("audio/mp4") ||
          normalized.includes("audio/m4a")
        ) {
          return "m4a";
        }
        if (normalized.includes("audio/mpeg")) return "mp3";
        if (normalized.includes("audio/wav")) return "wav";
        if (normalized.includes("audio/aac")) return "aac";
        if (normalized.includes("audio/ogg")) return "ogg";
        if (normalized.includes("audio/flac")) return "flac";
        return null;
      };

      mixpanel.track("Voice Note Audio Download Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });

      setLoadingAudio(true);

      const response = await fetch(note.url);
      const contentTypeHeader = response.headers.get("content-type");
      const normalizedContentType = (contentTypeHeader || "").toLowerCase();
      if (!response.ok) {
        throw new Error(
          `Audio download failed: ${response.status} ${response.statusText}`,
        );
      }
      if (
        normalizedContentType &&
        !normalizedContentType.startsWith("audio/") &&
        !normalizedContentType.includes("application/octet-stream")
      ) {
        throw new Error(
          `Audio download returned non-audio content-type: ${contentTypeHeader}`,
        );
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error("Audio download returned empty file");
      }
      if (
        blob.type &&
        !blob.type.startsWith("audio/") &&
        blob.type !== "application/octet-stream"
      ) {
        throw new Error(
          `Downloaded file is not audio (blob type: ${blob.type})`,
        );
      }

      const extension =
        getAudioExtensionFromContentType(contentTypeHeader) ??
        getAudioExtensionFromUrl(note.url);
      const filename = generatePatientNoteFilename(
        patient.name,
        getNoteTypeLabel(note.type),
        formatDate(note.date),
        "Audio",
        extension,
      );

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);

      mixpanel.track("Voice Note Audio Downloaded", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });
    } catch (error) {
      console.error("Error downloading audio:", error);
      Alert.alert("Error", "Failed to download audio file. Please try again.");

      mixpanel.track("Voice Note Audio Download Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Error Message":
          error instanceof Error ? error.message : "Unknown error",
      });

      logVoiceNotesUIError("Audio download failed", {
        noteId: note.id,
        noteType: note.type,
        noteUrl: note.url,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setLoadingAudio(false);
    }
  };

  const handleSaveSummary = async (note: VoiceNote): Promise<void> => {
    await updateNote(note.id, editedSummary, note);
    handleCancelEdit();
  };

  const handleDownloadSummary = (note: VoiceNote) => {
    try {
      mixpanel.track("Voice Note Summary Download Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });

      const filename = generatePatientNoteFilename(
        patient.name,
        getNoteTypeLabel(note.type),
        formatDate(note.date),
      );

      generatePDFDocument({
        title: `Voice Note - ${getNoteTypeLabel(note.type)}`,
        subtitle: `Date: ${formatDate(note.date)} at ${formatTime(note.date)}`,
        patientName: patient.name,
        content: note.note,
        filename: filename,
      });

      mixpanel.track("Voice Note Summary Downloaded", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });
    } catch (error) {
      Alert.alert("Error", "Failed to generate PDF. Please try again.");

      mixpanel.track("Voice Note Summary Download Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Error Message":
          error instanceof Error ? error.message : "Unknown error",
      });

      logVoiceNotesUIError("Summary PDF generation failed", {
        noteId: note.id,
        noteType: note.type,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handleSignDocument = (note: VoiceNote) => {
    setCurrentNoteForSignature(note);
    setSignatureName("");
    setSignatureDateTimeOverride(toDateTimeLocalValue(note.signed_at));
    setSignatureError(null);
    setShowSignatureModal(true);

    mixpanel.track("Voice Note Signature Modal Opened", {
      "User Email": session?.userEmail,
      "Clinic Name": session?.clinicName,
      "Patient ID": patient.clinicOSUUID,
      "Patient Name": patient.name,
      "Note ID": note.id,
    });
  };

  const handleSelectSavedSignature = (value: string) => {
    if (signatureError) setSignatureError(null);
    setSelectedSavedSignature(value);
    setSignatureName(value);
  };

  const handleSaveSignature = async () => {
    if (!session?.clinicId) {
      Alert.alert("Error", "Clinic session is required to save signatures.");
      return;
    }
    const clinicId = session.clinicId;

    if (!trimmedSignature) {
      Alert.alert("Error", "Please enter a signature before saving.");
      return;
    }

    if (!canSaveSignature) {
      return;
    }

    setSavingSignature(true);
    setSignatureError(null);

    try {
      const response = signatureRecordId
        ? await updateClinicNoteSignature(session, trimmedSignature)
        : await createClinicNoteSignature(session, trimmedSignature);

      setSignatureRecordId(response.id);
      setSavedSignatures((prev) => {
        const merged = dedupeSignatures([trimmedSignature, ...prev]);
        writeStoredSignatures(clinicId, merged);
        return merged;
      });
      setSelectedSavedSignature(trimmedSignature);

      Alert.alert("Saved", "Signature saved for reuse.");
    } catch (error) {
      setSignatureError("Unable to save signature. Please try again.");
      logVoiceNotesUIError("Clinic note signature save failed", {
        error: error instanceof Error ? error.message : String(error),
        signature: trimmedSignature,
      });
    } finally {
      setSavingSignature(false);
    }
  };

  const getLatestSignedPdfUrl = async (
    noteId: number,
  ): Promise<string | null> => {
    if (!session?.clinicId || !patient.clinicOSUUID) {
      return null;
    }

    const latestNotes = await getPatientNotes(
      session.clinicId,
      patient.clinicOSUUID,
    );
    const latestNote = latestNotes.find((item) => item.id === noteId);
    return latestNote?.signed_pdf_url ?? null;
  };

  const downloadSignedPDF = async (
    note: VoiceNote,
    options: {
      signedPdfUrl?: string | null;
      signatureInfo?: { signatureText: string; signatureDate: string };
    },
  ) => {
    try {
      mixpanel.track("Voice Note Signed PDF Download Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });

      const filename = generatePatientNoteFilename(
        patient.name,
        getNoteTypeLabel(note.type),
        formatDate(note.date),
        "Signed",
      );

      if (options.signedPdfUrl) {
        const response = await fetch(options.signedPdfUrl);
        if (!response.ok) {
          throw new Error(
            `Failed to download signed PDF URL: ${response.status}`,
          );
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = `${filename}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      } else if (options.signatureInfo) {
        generatePDFDocument({
          title: `Voice Note - ${getNoteTypeLabel(note.type)}`,
          subtitle: `Date: ${formatDate(note.date)} at ${formatTime(note.date)}`,
          patientName: patient.name,
          content: note.note,
          filename,
          signature: {
            signatureText: options.signatureInfo.signatureText,
            signatureDate: options.signatureInfo.signatureDate,
          },
        });
      } else {
        throw new Error("No signed PDF source available");
      }

      mixpanel.track("Voice Note Signed PDF Downloaded", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });
    } catch (error) {
      Alert.alert("Error", "Failed to download signed PDF. Please try again.");

      mixpanel.track("Voice Note Signed PDF Download Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Error Message":
          error instanceof Error ? error.message : "Unknown error",
      });

      logVoiceNotesUIError("Signed PDF download failed", {
        noteId: note.id,
        noteType: note.type,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handleSignOnly = async () => {
    if (!signatureName.trim()) {
      Alert.alert("Error", "Please enter your name for the signature");
      return;
    }

    const note = currentNoteForSignature;
    if (!note) return;

    setSigningInProgress(true);

    try {
      mixpanel.track("Voice Note Signature Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });

      if (!session?.clinicId || !patient.clinicOSUUID) {
        throw new Error("Missing clinic or patient context for note signing");
      }

      const parsedNoteId = Number(note.id);
      if (!Number.isFinite(parsedNoteId)) {
        throw new Error(`Invalid note ID for signing: ${note.id}`);
      }

      let signedAtOverride: string | null = null;
      if (signatureDateTimeOverride) {
        const parsedOverride = new Date(signatureDateTimeOverride);
        if (Number.isNaN(parsedOverride.getTime())) {
          throw new Error("Invalid signature date/time override");
        }
        signedAtOverride = parsedOverride.toISOString();
      }

      const signedNote = await signPatientNote(
        session.clinicId,
        patient.clinicOSUUID,
        parsedNoteId,
        {
          signedAtOverride,
        },
      );

      const signatureDate = signedNote.signed_at
        ? formatDate(signedNote.signed_at)
        : formatTodayDate();

      setSignedNotes((prev) => ({
        ...prev,
        [note.id]: {
          isSigned: true,
          signatureText: signatureName.trim(),
          signatureDate,
        },
      }));

      let signedPdfUrl = signedNote.signed_pdf_url ?? null;
      if (!signedPdfUrl) {
        signedPdfUrl = await getLatestSignedPdfUrl(parsedNoteId);
      }

      await fetchVoiceNotes();

      await downloadSignedPDF(note, {
        signedPdfUrl,
        signatureInfo: {
          signatureText: signatureName.trim(),
          signatureDate,
        },
      });

      mixpanel.track("Voice Note Signed", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Note Type": note.type,
      });

      setShowSignatureModal(false);
      setCurrentNoteForSignature(null);
      setSignatureName("");
      setSignatureDateTimeOverride("");
      Alert.alert("Success", "Document signed successfully");
    } catch (error) {
      Alert.alert("Error", "Failed to sign document. Please try again.");

      mixpanel.track("Voice Note Signature Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": note.id,
        "Error Message":
          error instanceof Error ? error.message : "Unknown error",
      });

      logVoiceNotesUIError("Voice note signing failed", {
        noteId: note.id,
        noteType: note.type,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setSigningInProgress(false);
    }
  };

  const handleDownloadSignedPDF = async (note: VoiceNote) => {
    const localSignatureInfo = signedNotes[note.id];
    const isSigned = Boolean(note.is_signed || localSignatureInfo?.isSigned);

    if (!isSigned) {
      Alert.alert(
        "Error",
        "This document must be signed before downloading as a signed PDF",
      );
      return;
    }

    let signedPdfUrl = note.signed_pdf_url ?? null;
    const parsedNoteId = Number(note.id);

    if (
      !signedPdfUrl &&
      note.is_signed &&
      Number.isFinite(parsedNoteId) &&
      session?.clinicId &&
      patient.clinicOSUUID
    ) {
      signedPdfUrl = await getLatestSignedPdfUrl(parsedNoteId);
      if (signedPdfUrl) {
        await fetchVoiceNotes();
      }
    }

    await downloadSignedPDF(note, {
      signedPdfUrl,
      signatureInfo: localSignatureInfo,
    });
  };

  const handleDeleteNote = (note: VoiceNote): void => {
    setNoteToDelete(note);
    setDeleteModalVisible(true);

    mixpanel.track("Voice Note Delete Modal Opened", {
      "User Email": session?.userEmail,
      "Clinic Name": session?.clinicName,
      "Patient ID": patient.clinicOSUUID,
      "Patient Name": patient.name,
      "Note ID": note.id,
      "Note Type": note.type,
    });
  };

  const handleOpenReprocessModal = (note: VoiceNote) => {
    if (!note.audioId) {
      Alert.alert("Error", "This note has no audio source to reprocess.");
      return;
    }

    const rawNoteDate =
      (note as any)?.note_json?.metadata?.note_date ??
      (note as any)?.note_json?.note_date ??
      note?.date;
    setReprocessNoteDate(toISODate(rawNoteDate));
    setRecoverIncompleteChunkedUpload(false);
    setReprocessTargetNote(note);
    setReprocessModalVisible(true);
  };

  const buildUnprocessedAudioNote = (
    audio: (typeof unprocessedAudios)[number],
  ): VoiceNote => {
    const syntheticNoteId = `unprocessed-audio-${audio.id}`;

    return {
      id: syntheticNoteId,
      audioId: audio.id.toString(),
      filename: `audio-${audio.id}`,
      transcription: "",
      note: "",
      note_json: {},
      type: "Not processed",
      date: audio.date_of_recording ?? audio.created_at,
      url: audio.get_url,
    };
  };

  const openUnprocessedAudioReprocessModal = (
    audio: (typeof unprocessedAudios)[number],
    recoverIncompleteUpload: boolean,
  ) => {
    setReprocessNoteDate(
      toISODate(audio.date_of_recording ?? audio.created_at),
    );
    setRecoverIncompleteChunkedUpload(recoverIncompleteUpload);
    setReprocessTargetNote(buildUnprocessedAudioNote(audio));
    setReprocessModalVisible(true);
  };

  const handleOpenUnprocessedAudioReprocessModal = (
    audio: (typeof unprocessedAudios)[number],
  ) => {
    if (!shouldOfferIncompleteChunkRecovery(audio)) {
      openUnprocessedAudioReprocessModal(audio, false);
      return;
    }

    setRecoveryConfirmAudio(audio);
  };

  const canBrowserPlayAudioUrl = (url?: string | null): boolean => {
    if (!url) return false;
    if (typeof document === "undefined") return true;

    const audioEl = document.createElement("audio");
    const normalizedPath = url.split("?")[0].toLowerCase();

    if (normalizedPath.endsWith(".webm")) {
      return audioEl.canPlayType("audio/webm") !== "";
    }
    if (normalizedPath.endsWith(".m4a") || normalizedPath.endsWith(".mp4")) {
      return audioEl.canPlayType("audio/mp4") !== "";
    }
    if (normalizedPath.endsWith(".mp3")) {
      return audioEl.canPlayType("audio/mpeg") !== "";
    }
    if (normalizedPath.endsWith(".wav")) {
      return audioEl.canPlayType("audio/wav") !== "";
    }
    if (normalizedPath.endsWith(".ogg")) {
      return audioEl.canPlayType("audio/ogg") !== "";
    }

    return true;
  };

  const handlePlayUnprocessedAudio = async (
    audio: (typeof unprocessedAudios)[number],
  ) => {
    const syntheticNote = buildUnprocessedAudioNote(audio);

    if (!audio.get_url) {
      Alert.alert("Error", "Audio file not uploaded");
      logVoiceNotesUIError("Unprocessed audio has no presigned URL", {
        audioId: audio.id,
        isUploaded: audio.is_uploaded,
      });
      return;
    }

    if (!canBrowserPlayAudioUrl(audio.get_url)) {
      Alert.alert("Error", "This browser cannot play this audio format.");
      logVoiceNotesUIError("Unprocessed audio format unsupported by browser", {
        audioId: audio.id,
        isUploaded: audio.is_uploaded,
        audioUrl: audio.get_url,
      });
      return;
    }

    // Incomplete recordings can still have playable audio before finalization.
    await handlePlayPause(syntheticNote);
  };

  const handleReprocessAsType = async (
    selectedType: string,
    selectedNoteDate: string,
  ) => {
    const targetNote = reprocessTargetNote;

    if (
      !session ||
      !session.clinicId ||
      !patient?.clinicOSUUID ||
      !targetNote?.audioId
    ) {
      Alert.alert("Error", "Missing data needed to reprocess this note.");
      return;
    }

    const audioId = Number(targetNote.audioId);
    if (!Number.isFinite(audioId) || audioId <= 0) {
      Alert.alert("Error", "Invalid audio ID for this note.");
      return;
    }

    setReprocessingNoteId(targetNote.id);
    setReprocessModalVisible(false);
    setReprocessProgressText(
      recoverIncompleteChunkedUpload
        ? "Recovering and reprocessing audio..."
        : "Reprocessing audio...",
    );
    try {
      mixpanel.track("Voice Note Reprocess Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Original Note ID": targetNote.id,
        "Original Note Type": targetNote.type,
        "Audio ID": audioId,
        "Selected Note Type": selectedType,
        "Recover Incomplete Upload": recoverIncompleteChunkedUpload,
      });

      const result = await reprocessAudioAsDifferentNoteType({
        session,
        audioId,
        patientId: patient.clinicOSUUID,
        encounterType: selectedType,
        appointmentId: appointmentId ?? undefined,
        noteDateOverride: selectedNoteDate || undefined,
        recoverIncompleteChunkedUpload,
        onProgress: (stage) => {
          if (stage === "polling_notes") {
            setReprocessProgressText("Finalizing and refreshing notes...");
          }
        },
      });

      if (result.status === "completed") {
        mixpanel.track("Voice Note Reprocess Succeeded", {
          "User Email": session?.userEmail,
          "Clinic Name": session?.clinicName,
          "Patient ID": patient.clinicOSUUID,
          "Patient Name": patient.name,
          "Original Note ID": targetNote.id,
          "Original Note Type": targetNote.type,
          "Audio ID": audioId,
          "Selected Note Type": selectedType,
          "Recovered Incomplete Upload": recoverIncompleteChunkedUpload,
        });

        Alert.alert(
          "Success",
          "Audio reprocessed with the selected note type.",
        );
      } else {
        Alert.alert(
          "Still Processing",
          "Reprocessing started, but the new note is still finishing. It should appear shortly.",
        );
      }

      setReprocessTargetNote(null);
      setRecoverIncompleteChunkedUpload(false);
      setReprocessProgressText(null);
      await fetchVoiceNotes();
    } catch (error) {
      console.error("Error reprocessing note:", error);
      Alert.alert(
        recoverIncompleteChunkedUpload ? "Unable to Recover Audio" : "Error",
        recoverIncompleteChunkedUpload && error instanceof Error
          ? error.message
          : "Failed to reprocess note. Please try again.",
      );

      mixpanel.track("Voice Note Reprocess Failed", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Original Note ID": targetNote.id,
        "Original Note Type": targetNote.type,
        "Audio ID": audioId,
        "Selected Note Type": selectedType,
        "Recover Incomplete Upload": recoverIncompleteChunkedUpload,
        Error: error instanceof Error ? error.message : "Unknown error",
      });

      logVoiceNotesUIError("Voice note reprocess failed", {
        noteId: targetNote.id,
        audioId,
        selectedType,
        error: error instanceof Error ? error.message : String(error),
      });
      setReprocessProgressText(null);
    } finally {
      setReprocessingNoteId(null);
    }
  };

  const confirmDeleteNote = async (): Promise<void> => {
    if (!noteToDelete) return;

    setDeleting(true);
    try {
      await deleteNote(noteToDelete.id);

      if (signedNotes[noteToDelete.id]) {
        setSignedNotes((prev) => {
          const newSignedNotes = { ...prev };
          delete newSignedNotes[noteToDelete.id];
          return newSignedNotes;
        });
      }

      setDeleteModalVisible(false);
      setNoteToDelete(null);
    } catch (error) {
      // Error handling is done in the hook
    } finally {
      setDeleting(false);
    }
  };

  return (
    <View style={styles.container}>
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color="#0085FF" />
          <Text style={styles.loadingText}>Loading voice notes...</Text>
        </View>
      ) : error ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={fetchVoiceNotes}
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : voiceNotes.length > 0 || unprocessedAudios.length > 0 ? (
        <View>
          {reprocessProgressText ? (
            <View style={styles.reprocessStatusContainer}>
              <ActivityIndicator size="small" color="#0085FF" />
              <Text style={styles.reprocessStatusText}>
                {reprocessProgressText}
              </Text>
            </View>
          ) : null}
          {voiceNotes.length > 0 ? (
            <View style={[styles.notesGrid, notesContainerStyle]}>
              {voiceNotes.map((note, index) => (
                <VoiceNoteCard
                  key={note.id}
                  note={note}
                  style={getItemStyle(index)}
                  playingNoteId={playingNoteId}
                  loadingAudio={loadingAudio}
                  editingNoteId={editingNoteId}
                  editingField={editingField}
                  editedSummary={editedSummary}
                  expandedSummaries={expandedSummaries}
                  signedNotes={signedNotes}
                  saving={saving}
                  tipsEnabled={tipsEnabled}
                  tipsState={tipsState}
                  getNoteTypeLabel={getNoteTypeLabel}
                  onPlayPause={handlePlayPause}
                  onDownloadSummary={handleDownloadSummary}
                  onDownloadSignedPDF={handleDownloadSignedPDF}
                  onSignDocument={handleSignDocument}
                  onDeleteNote={handleDeleteNote}
                  onEditSummary={handleEditSummary}
                  onSaveSummary={handleSaveSummary}
                  onCancelEdit={handleCancelEdit}
                  onToggleSummary={toggleSummary}
                  onDownloadTranscript={downloadTranscript}
                  onDownloadAudio={downloadAudio}
                  onReprocessNote={handleOpenReprocessModal}
                  reprocessingNoteId={reprocessingNoteId}
                  onMarkStepComplete={markStepComplete}
                  setEditedSummary={setEditedSummary}
                  TOOLTIP_STEPS={TOOLTIP_STEPS}
                />
              ))}
            </View>
          ) : null}

          {unprocessedAudios.length > 0 ? (
            <View style={styles.unprocessedSection}>
              <Text style={styles.unprocessedSectionTitle}>Not processed</Text>
              <Text style={styles.unprocessedSectionSubtitle}>
                Audio files with no voice note yet.
              </Text>
              <View style={[styles.notesGrid, notesContainerStyle]}>
                {unprocessedAudios.map((audio, index) => {
                  const syntheticNote = buildUnprocessedAudioNote(audio);
                  const syntheticNoteId = syntheticNote.id;
                  const isReprocessing = reprocessingNoteId === syntheticNoteId;
                  const isPlayingThisAudio = playingNoteId === syntheticNoteId;
                  const canPlayAudio = canBrowserPlayAudioUrl(audio.get_url);
                  const displayDateLabel = audio.date_of_recording
                    ? audio.date_of_recording
                    : formatDate(audio.created_at);
                  const createdAtMs = new Date(audio.created_at).getTime();
                  const canShowReprocessButton =
                    Number.isFinite(createdAtMs) &&
                    Date.now() - createdAtMs >= 10 * 60 * 1000;

                  return (
                    <View
                      key={syntheticNoteId}
                      style={[styles.unprocessedCard, getItemStyle(index)]}
                    >
                      <View style={styles.unprocessedCardHeader}>
                        <Text style={styles.unprocessedStatusLabel}>
                          Not processed
                        </Text>
                        <Text style={styles.unprocessedDateLabel}>
                          {displayDateLabel}
                        </Text>
                      </View>

                      <Text style={styles.unprocessedAudioId}>
                        Audio ID: {audio.id}
                      </Text>

                      <View style={styles.unprocessedActionRow}>
                        <TouchableOpacity
                          style={[
                            styles.unprocessedPlayButton,
                            !canPlayAudio &&
                              styles.unprocessedPlayButtonDisabled,
                            loadingAudio && isPlayingThisAudio
                              ? styles.unprocessedPlayButtonLoading
                              : null,
                          ]}
                          onPress={() => handlePlayUnprocessedAudio(audio)}
                          disabled={!canPlayAudio || loadingAudio}
                          accessibilityRole="button"
                          accessibilityLabel={`${isPlayingThisAudio ? "Pause" : "Play"} audio ${audio.id}`}
                        >
                          {loadingAudio && isPlayingThisAudio ? (
                            <ActivityIndicator size="small" color="#FFFFFF" />
                          ) : isPlayingThisAudio ? (
                            <Pause size={20} color="#FFFFFF" />
                          ) : (
                            <Play size={20} color="#FFFFFF" />
                          )}
                        </TouchableOpacity>
                        {canShowReprocessButton || isReprocessing ? (
                          <TouchableOpacity
                            style={[
                              styles.unprocessedReprocessButton,
                              styles.unprocessedSecondaryActionSpacing,
                              isReprocessing &&
                                styles.unprocessedReprocessButtonDisabled,
                            ]}
                            onPress={() =>
                              handleOpenUnprocessedAudioReprocessModal(audio)
                            }
                            disabled={isReprocessing}
                            accessibilityRole="button"
                            accessibilityLabel={`Reprocess audio ${audio.id}`}
                          >
                            {isReprocessing ? (
                              <ActivityIndicator size="small" color="#FFFFFF" />
                            ) : (
                              <Text
                                style={styles.unprocessedReprocessButtonText}
                              >
                                Reprocess
                              </Text>
                            )}
                          </TouchableOpacity>
                        ) : null}
                      </View>
                    </View>
                  );
                })}
              </View>
            </View>
          ) : null}
        </View>
      ) : (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>
            No voice notes recorded for this patient.
          </Text>
        </View>
      )}

      {/* Modals */}
      <SignatureModal
        visible={showSignatureModal}
        isAlreadySigned={Boolean(
          currentNoteForSignature?.is_signed ||
            (currentNoteForSignature &&
              signedNotes[currentNoteForSignature.id]?.isSigned),
        )}
        signatureName={signatureName}
        signatureDateTimeOverride={signatureDateTimeOverride}
        savedSignatures={savedSignatures}
        selectedSavedSignature={selectedSavedSignature}
        loadingSignatures={loadingSignatures}
        signingInProgress={signingInProgress}
        savingSignature={savingSignature}
        canSaveSignature={canSaveSignature}
        isSignatureSaved={isSignatureSaved}
        signatureError={signatureError}
        onClose={() => {
          setShowSignatureModal(false);
          setCurrentNoteForSignature(null);
          setSignatureName("");
          setSignatureDateTimeOverride("");
        }}
        onSignatureName={(value) => {
          if (signatureError) setSignatureError(null);
          setSignatureName(value);
        }}
        onSignatureDateTimeOverrideChange={setSignatureDateTimeOverride}
        onSelectSavedSignature={handleSelectSavedSignature}
        onSaveSignature={handleSaveSignature}
        onSign={handleSignOnly}
      />

      <DeleteConfirmModal
        visible={deleteModalVisible}
        deleting={deleting}
        onClose={() => {
          setDeleteModalVisible(false);
          setNoteToDelete(null);
        }}
        onConfirm={confirmDeleteNote}
      />

      <IncompleteChunkRecoveryModal
        visible={Boolean(recoveryConfirmAudio)}
        onCancel={() => setRecoveryConfirmAudio(null)}
        onRecover={() => {
          if (!recoveryConfirmAudio) return;
          const audio = recoveryConfirmAudio;
          setRecoveryConfirmAudio(null);
          openUnprocessedAudioReprocessModal(audio, true);
        }}
      />

      {/* EMR Sync Modal */}
      {emrSyncModalVisible && selectedNoteForSync && appointmentId && (
        <EMRSyncModal
          visible={emrSyncModalVisible}
          onClose={() => {
            setEmrSyncModalVisible(false);
            setSelectedNoteForSync(null);
          }}
          noteId={selectedNoteForSync.id}
          appointmentId={appointmentId}
          noteJson={selectedNoteForSync.note_json}
          originalNoteText={selectedNoteForSync.note}
          session={session}
          patient={patient}
          onSyncSuccess={() => {
            fetchVoiceNotes();
          }}
        />
      )}

      <EncounterTypeModal
        visible={reprocessModalVisible}
        onClose={() => {
          if (!reprocessingNoteId) {
            setReprocessModalVisible(false);
            setReprocessTargetNote(null);
          }
        }}
        onSelect={handleReprocessAsType}
        loading={loadingNoteTypes}
        error={noteTypesError}
        types={availableNoteTypes}
        allowCreate
        onCreateType={async (name: string) => {
          await createNoteType(name);
        }}
        noteDate={reprocessNoteDate}
        maxDate={getTodayLocalISODate()}
        onChangeNoteDate={setReprocessNoteDate}
        disableNoteDate={Boolean(reprocessingNoteId)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 0,
  },
  notesGrid: {
    width: "100%",
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  loadingText: {
    marginLeft: 8,
    fontSize: 14,
    color: "#666666",
  },
  errorContainer: {
    padding: 16,
    backgroundColor: "#FFF2F0",
    borderRadius: 8,
    alignItems: "center",
  },
  errorText: {
    fontSize: 14,
    color: "#E53935",
    marginBottom: 8,
  },
  retryButton: {
    backgroundColor: "#0085FF",
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
  },
  retryButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "500",
  },
  emptyContainer: {
    padding: 24,
    backgroundColor: "#F5F5F5",
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: 120,
  },
  emptyText: {
    fontSize: 14,
    color: "#777777",
  },
  reprocessStatusContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: "#BFDBFE",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 10,
  },
  reprocessStatusText: {
    fontSize: 13,
    color: "#1D4ED8",
    marginLeft: 8,
  },
  unprocessedSection: {
    marginTop: 16,
  },
  unprocessedSectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333333",
    marginBottom: 4,
  },
  unprocessedSectionSubtitle: {
    fontSize: 13,
    color: "#666666",
    marginBottom: 10,
  },
  unprocessedCard: {
    backgroundColor: "#FFF7ED",
    borderWidth: 1,
    borderColor: "#FED7AA",
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  unprocessedCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  unprocessedStatusLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#9A3412",
  },
  unprocessedDateLabel: {
    fontSize: 12,
    color: "#9A3412",
  },
  unprocessedAudioId: {
    fontSize: 13,
    color: "#7C2D12",
    marginBottom: 10,
  },
  unprocessedActionRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  unprocessedSecondaryActionSpacing: {
    marginLeft: 8,
  },
  unprocessedPlayButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#0085FF",
    alignItems: "center",
    justifyContent: "center",
  },
  unprocessedPlayButtonDisabled: {
    backgroundColor: "#CFCFCF",
  },
  unprocessedPlayButtonLoading: {
    opacity: 0.7,
  },
  unprocessedReprocessButton: {
    minHeight: 44,
    flex: 1,
    borderRadius: 6,
    backgroundColor: "#C2410C",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  unprocessedReprocessButtonDisabled: {
    backgroundColor: "#CFCFCF",
  },
  unprocessedReprocessButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
});

export default VoiceNotesList;
